import Dexie, { Table } from 'dexie';
import { Database } from '../types/database';

// Define types for our local database
type Card = Database['public']['Tables']['cards']['Row'];
type UserCardProgress = Database['public']['Tables']['user_card_progress']['Row'];
type StudySession = Database['public']['Tables']['study_sessions']['Row'];
type Lesson = Database['public']['Tables']['lessons']['Row'];
type LessonCard = Database['public']['Tables']['lesson_cards']['Row'];
type UserLessonProgress = Database['public']['Tables']['user_lesson_progress']['Row'];
type CardReviewHistory = Database['public']['Tables']['card_review_history']['Row'];
type Source = Database['public']['Tables']['sources']['Row'];
type Exercise = Database['public']['Tables']['exercises']['Row'];
type ExerciseLesson = Database['public']['Tables']['exercise_lessons']['Row'];

// Generic sync metadata added to all local records
export type Syncable<T> = T & {
  _syncStatus?: 'synced' | 'pending' | 'error';
  _lastSyncError?: string;
}

// Type aliases for each entity (used by components and utilities)
export type LocalCard = Syncable<Card>;
export type LocalUserCardProgress = Syncable<UserCardProgress>;
export type LocalStudySession = Syncable<StudySession>;
export type LocalLesson = Syncable<Lesson>;
export type LocalLessonCard = Syncable<LessonCard>;
export type LocalUserLessonProgress = Syncable<UserLessonProgress>;
export type LocalCardReviewHistory = Syncable<CardReviewHistory>;
export type LocalSource = Syncable<Source>;
export type LocalExercise = Syncable<Exercise>;
export type LocalExerciseLesson = Syncable<ExerciseLesson>;

export interface SyncMetadata {
  id?: number;
  tableName: 'cards' | 'user_card_progress' | 'study_sessions' | 'lessons' | 'lesson_cards' | 'user_lesson_progress' | 'card_review_history' | 'sources' | 'exercises' | 'exercise_lessons';
  lastSync: Date;
  pendingChanges: number;
}

export interface SyncQueue {
  id?: number;
  tableName: string;
  recordId: string;
  operation: 'create' | 'update' | 'delete';
  data: any;
  timestamp: Date;
  attempts: number;
  lastError?: string;
}

// Create the database class
class FlashcardDatabase extends Dexie {
  cards!: Table<LocalCard>;
  userCardProgress!: Table<LocalUserCardProgress>;
  studySessions!: Table<LocalStudySession>;
  lessons!: Table<LocalLesson>;
  lessonCards!: Table<LocalLessonCard>;
  userLessonProgress!: Table<LocalUserLessonProgress>;
  cardReviewHistory!: Table<LocalCardReviewHistory>;
  sources!: Table<LocalSource>;
  exercises!: Table<LocalExercise>;
  exerciseLessons!: Table<LocalExerciseLesson>;
  syncMetadata!: Table<SyncMetadata>;
  syncQueue!: Table<SyncQueue>;

  constructor() {
    super('FlashcardSRSDatabase');
    
    this.version(1).stores({
      // Primary key is 'id' for cards and study_sessions
      cards: 'id, user_id, created_at, updated_at, [user_id+updated_at]',
      
      // Composite primary key for user_card_progress
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      
      // Primary key is 'id' for study_sessions
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      
      // Sync metadata
      syncMetadata: '++id, tableName',
      
      // Sync queue for offline changes
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Version 2: Remove user_id from cards (shared cards system)
    this.version(2).stores({
      // Cards no longer have user_id - they're shared
      cards: 'id, created_at, updated_at',
      
      // User progress still has user_id
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      
      // Study sessions still have user_id
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      
      // Sync metadata
      syncMetadata: '++id, tableName',
      
      // Sync queue for offline changes
      syncQueue: '++id, tableName, recordId, timestamp'
    }).upgrade(trans => {
      // Migration: Clear all cards data since structure changed
      return trans.table('cards').clear();
    });

    // Version 3: Add lessons support
    this.version(3).stores({
      // Existing tables remain the same
      cards: 'id, created_at, updated_at',
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      studySessions: 'id, user_id, started_at, [user_id+started_at]',

      // New lesson tables
      lessons: 'id, slug, is_published, language, level, created_at, updated_at',
      lessonCards: '[lesson_id+card_id], lesson_id, card_id, order_index',
      userLessonProgress: '[user_id+lesson_id], user_id, lesson_id, status, updated_at',

      // Sync tables
      syncMetadata: '++id, tableName',
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Version 4: Add card review history for offline support
    this.version(4).stores({
      // Existing tables remain the same
      cards: 'id, created_at, updated_at',
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      lessons: 'id, slug, is_published, language, level, created_at, updated_at',
      lessonCards: '[lesson_id+card_id], lesson_id, card_id, order_index',
      userLessonProgress: '[user_id+lesson_id], user_id, lesson_id, status, updated_at',

      // New card review history table
      cardReviewHistory: 'id, [user_id+card_id], user_id, card_id, reviewed_at, [user_id+reviewed_at]',

      // Sync tables
      syncMetadata: '++id, tableName',
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Version 5: Add sources table and source_id to cards
    this.version(5).stores({
      // Updated cards with source_id index
      cards: 'id, source_id, created_at, updated_at',
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      lessons: 'id, slug, is_published, language, level, created_at, updated_at',
      lessonCards: '[lesson_id+card_id], lesson_id, card_id, order_index',
      userLessonProgress: '[user_id+lesson_id], user_id, lesson_id, status, updated_at',
      cardReviewHistory: 'id, [user_id+card_id], user_id, card_id, reviewed_at, [user_id+reviewed_at]',

      // New sources table
      sources: 'id, slug, parent_id, category, [category+parent_id+slug], created_at, updated_at',

      // Sync tables
      syncMetadata: '++id, tableName',
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Version 6: Add exercises and exercise_lessons tables, exercise_id index on cards
    this.version(6).stores({
      cards: 'id, source_id, exercise_id, created_at, updated_at',
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      lessons: 'id, slug, is_published, language, level, created_at, updated_at',
      lessonCards: '[lesson_id+card_id], lesson_id, card_id, order_index',
      userLessonProgress: '[user_id+lesson_id], user_id, lesson_id, status, updated_at',
      cardReviewHistory: 'id, [user_id+card_id], user_id, card_id, reviewed_at, [user_id+reviewed_at]',
      sources: 'id, slug, parent_id, category, [category+parent_id+slug], created_at, updated_at',
      exercises: 'id, slug, category, source_id, created_at, updated_at',
      exerciseLessons: '[exercise_id+lesson_id], exercise_id, lesson_id',
      syncMetadata: '++id, tableName',
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Version 7: Add sequential_enabled to exercises
    this.version(7).stores({
      cards: 'id, source_id, exercise_id, created_at, updated_at',
      userCardProgress: '[user_id+card_id], user_id, card_id, updated_at, [user_id+updated_at]',
      studySessions: 'id, user_id, started_at, [user_id+started_at]',
      lessons: 'id, slug, is_published, language, level, created_at, updated_at',
      lessonCards: '[lesson_id+card_id], lesson_id, card_id, order_index',
      userLessonProgress: '[user_id+lesson_id], user_id, lesson_id, status, updated_at',
      cardReviewHistory: 'id, [user_id+card_id], user_id, card_id, reviewed_at, [user_id+reviewed_at]',
      sources: 'id, slug, parent_id, category, [category+parent_id+slug], created_at, updated_at',
      exercises: 'id, slug, category, source_id, created_at, updated_at',
      exerciseLessons: '[exercise_id+lesson_id], exercise_id, lesson_id',
      syncMetadata: '++id, tableName',
      syncQueue: '++id, tableName, recordId, timestamp'
    });

    // Bind tables
    this.cards = this.table('cards');
    this.userCardProgress = this.table('userCardProgress');
    this.studySessions = this.table('studySessions');
    this.lessons = this.table('lessons');
    this.lessonCards = this.table('lessonCards');
    this.userLessonProgress = this.table('userLessonProgress');
    this.cardReviewHistory = this.table('cardReviewHistory');
    this.sources = this.table('sources');
    this.exercises = this.table('exercises');
    this.exerciseLessons = this.table('exerciseLessons');
    this.syncMetadata = this.table('syncMetadata');
    this.syncQueue = this.table('syncQueue');
  }

  // Initialize sync metadata for each table
  async initializeSyncMetadata() {
    const tables: Array<'cards' | 'user_card_progress' | 'study_sessions' | 'lessons' | 'lesson_cards' | 'user_lesson_progress' | 'card_review_history' | 'sources' | 'exercises' | 'exercise_lessons'> = [
      'cards',
      'user_card_progress',
      'study_sessions',
      'lessons',
      'lesson_cards',
      'user_lesson_progress',
      'card_review_history',
      'sources',
      'exercises',
      'exercise_lessons'
    ];

    for (const tableName of tables) {
      const existing = await this.syncMetadata.where('tableName').equals(tableName).first();
      if (!existing) {
        await this.syncMetadata.add({
          tableName,
          lastSync: new Date(0), // Never synced
          pendingChanges: 0
        });
      }
    }
  }

  // Add operation to sync queue
  async addToSyncQueue(
    tableName: string,
    recordId: string,
    operation: 'create' | 'update' | 'delete',
    data: any
  ) {
    await this.syncQueue.add({
      tableName,
      recordId,
      operation,
      data,
      timestamp: new Date(),
      attempts: 0
    });

    // Update pending changes count
    const metadata = await this.syncMetadata.where('tableName').equals(tableName).first();
    if (metadata) {
      await this.syncMetadata.update(metadata.id!, {
        pendingChanges: (metadata.pendingChanges || 0) + 1
      });
    }
  }

  // Clear the database (useful for logout)
  async clearAllData() {
    await this.cards.clear();
    await this.userCardProgress.clear();
    await this.studySessions.clear();
    await this.lessons.clear();
    await this.lessonCards.clear();
    await this.userLessonProgress.clear();
    await this.cardReviewHistory.clear();
    await this.sources.clear();
    await this.exercises.clear();
    await this.exerciseLessons.clear();
    await this.syncQueue.clear();
    // Don't clear syncMetadata - keep track of last sync times
  }
}

// Create a singleton instance
export const db = new FlashcardDatabase();

// Initialize the database
export async function initializeLocalDb() {
  try {
    await db.open();
    await db.initializeSyncMetadata();
    console.log('Local database initialized successfully');
  } catch (error) {
    console.error('Failed to initialize local database:', error);
    throw error;
  }
}